import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import hubServiceApi from 'shared/api/hub.service.api';
import { INTERNAL_ROUTING } from 'shared/constants';

const KnowledgeHubContext = createContext();

export function useKnowledgeHub() {
  return useContext(KnowledgeHubContext);
}

/**
 * Check if error is an unauthorized/forbidden error (403)
 * These should be treated as "not found" to avoid exposing authorization details
 */
function isUnauthorizedError(error) {
  return error?.response?.status === 403;
}

/**
 * Knowledge Hub Provider
 * @export
 * @param {{
 * children: React.ReactNode;
 * analyticsTriggers: {
 *  onPageLoad: (payload) => void;
 *  onSelectOrganization: (payload) => void;
 * }
 * }} { children, analyticsTriggers }
 * @returns
 */
export function KnowledgeHubProvider({ children, analyticsTriggers }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const organizationId = searchParams.get(INTERNAL_ROUTING.QUERY_PARAMS.HUB_ORGANIZATION) ?? '';
  const workspaceId = searchParams.get(INTERNAL_ROUTING.QUERY_PARAMS.HUB_WORKSPACE) ?? '';
  const categoryId = searchParams.get(INTERNAL_ROUTING.QUERY_PARAMS.HUB_CATEGORY) ?? '';
  const binderId = searchParams.get(INTERNAL_ROUTING.QUERY_PARAMS.HUB) ?? '';

  const [organizationsList, setOrganizationsList] = useState([]);
  const [workspacesList, setWorkspacesList] = useState([]);
  const [knowledgeCategories, setKnowledgeCategories] = useState([]);
  const [previousParentCategories, setPreviousParentCategories] = useState([]);
  const [parentCategory, setParentCategory] = useState(null);

  const parentCategoryRef = useRef(null);
  // Fetched categories (used by breadcrumbs and other lookups)
  const [fetchedCategories, setFetchedCategories] = useState({});
  const fetchedCategoriesRef = useRef({});
  const previousParentCategoriesRef = useRef([]);

  // Keep refs in sync with state for use in stable callbacks
  useEffect(() => {
    parentCategoryRef.current = parentCategory;
  }, [parentCategory]);

  useEffect(() => {
    fetchedCategoriesRef.current = fetchedCategories;
  }, [fetchedCategories]);

  useEffect(() => {
    previousParentCategoriesRef.current = previousParentCategories;
  }, [previousParentCategories]);

  const [binder, setBinder] = useState(null);
  const [sectionTitle, setSectionTitle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingBinder, setLoadingBinder] = useState(false);
  const [error, setError] = useState(null);

  const addParamToUrl = useCallback(
    (key, value) => {
      setSearchParams(prev => {
        prev.set(key, value);
        return prev;
      });
    },
    [setSearchParams],
  );

  const replaceParamInUrl = useCallback(
    (key, value) => {
      setSearchParams(
        prev => {
          prev.set(key, value);
          return prev;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  /**
   * set section title depending on the selected category, workspace or organization
   */
  useEffect(() => {
    let sectionTitle = null;

    if (categoryId) {
      sectionTitle = parentCategory?.title;
    } else if (workspaceId) {
      sectionTitle = workspacesList?.find(ws => ws.id === workspaceId)?.name;
    } else if (organizationId) {
      sectionTitle = organizationsList?.find(org => org.id === organizationId)?.name;
    }

    setSectionTitle(sectionTitle);
  }, [organizationId, workspaceId, categoryId, organizationsList, workspacesList, parentCategory]);

  /**
   * fetch functions
   */

  const getOrganizations = useCallback(async () => {
    setLoading(true);
    setError(null);

    const { data, error } = await hubServiceApi.getOrganizations();

    if (error || !data) {
      // Treat 403 as "not found" - show empty list instead of error
      if (isUnauthorizedError(error)) {
        setOrganizationsList([]);
        setLoading(false);
        return [];
      }
      setError(error);
      setLoading(false);
      return;
    }

    setOrganizationsList(data);
    setLoading(false);
    return data;
  }, []);

  const getWorkspaces = useCallback(async ({ organizationId }) => {
    setLoading(true);
    setError(null);

    const { data, error } = await hubServiceApi.getWorkspaces({ organizationId });

    if (error || !data) {
      // Treat 403 as "not found" - show empty list instead of error
      if (isUnauthorizedError(error)) {
        setWorkspacesList([]);
        setLoading(false);
        return [];
      }
      setError(error);
      setLoading(false);
      return;
    }

    setWorkspacesList(data);
    setLoading(false);
    return data;
  }, []);

  const getKnowledgeCategories = useCallback(
    async ({ organizationId, workspaceId, parentId = null }) => {
      // Check if category data is already cached (for back navigation via breadcrumbs)
      // Use refs to avoid callback recreation when cache changes
      const cachedCategory =
        previousParentCategoriesRef.current.find(c => c?.id === parentId) ||
        fetchedCategoriesRef.current[parentId];

      if (cachedCategory?.subcategories) {
        // Use cached data - save current parent before switching
        setPreviousParentCategories(prev => {
          if (!parentCategoryRef.current) return prev;
          if (prev.find(p => p?.id === parentCategoryRef.current.id)) return prev;
          return [...prev, parentCategoryRef.current];
        });
        setParentCategory(cachedCategory);
        setSectionTitle(cachedCategory.title);
        setKnowledgeCategories(cachedCategory.subcategories);
        return;
      }

      setLoading(true);
      setError(null);

      const { data, error } = await hubServiceApi.getKnowledgeCategories({
        organizationId,
        workspaceId,
        parentId,
      });

      if (error || !data) {
        // Treat 403 as "not found" - show empty categories instead of error
        if (isUnauthorizedError(error)) {
          setKnowledgeCategories([]);
          setLoading(false);
          return;
        }
        setError(error);
        setLoading(false);
        return;
      }

      // Use ref to avoid circular dependency (this callback sets parentCategory)
      setPreviousParentCategories(prev => {
        // Don't add null values
        if (!parentCategoryRef.current) return prev;
        // prevent duplicates
        if (prev.find(p => p?.id === parentCategoryRef.current.id)) return prev;
        return [...prev, parentCategoryRef.current];
      });
      setParentCategory(data);
      setSectionTitle(data.title);
      setKnowledgeCategories(data.subcategories);
      setLoading(false);
    },
    [],
  );

  const getCategory = useCallback(async ({ organizationId, workspaceId, categoryId }) => {
    // Check cache first - use refs to avoid callback recreation when cache changes
    if (fetchedCategoriesRef.current[categoryId]) {
      return fetchedCategoriesRef.current[categoryId];
    }

    // Check previousParentCategories
    const fromPrevious = previousParentCategoriesRef.current.find(c => c?.id === categoryId);
    if (fromPrevious) {
      return fromPrevious;
    }

    // Note: This function does NOT set loading state as it's used for background fetches (breadcrumbs)
    const { data, error } = await hubServiceApi.getCategory({
      organizationId,
      workspaceId,
      categoryId,
    });

    if (error || !data) {
      // Treat 403 as "not found"
      if (isUnauthorizedError(error)) {
        return null;
      }
      return null;
    }

    // Cache the fetched category
    setFetchedCategories(prev => ({ ...prev, [categoryId]: data }));

    return data;
  }, []);

  const getBinder = useCallback(async ({ organizationId, workspaceId, binderId }) => {
    setLoadingBinder(true);
    setError(null);

    const { data, error } = await hubServiceApi.getKnowledgeBinder({
      organizationId,
      workspaceId,
      categoryId: binderId,
    });

    if (error || !data) {
      // Treat 403 as "not found"
      if (isUnauthorizedError(error)) {
        setBinder(null);
        setLoadingBinder(false);
        return;
      }
      setError(error);
      setLoadingBinder(false);
      return;
    }

    setBinder(data);
    setLoadingBinder(false);
  }, []);

  /**
   * State reset and fetch effects
   */

  // Fetch organizations on mount
  useEffect(() => {
    getOrganizations();
  }, [getOrganizations]);

  // Reset child state when organization changes
  useEffect(() => {
    setWorkspacesList([]);
    setKnowledgeCategories([]);
    setParentCategory(null);
    setPreviousParentCategories([]);
    setBinder(null);
  }, [organizationId]);

  // Reset child state when workspace changes
  useEffect(() => {
    setKnowledgeCategories([]);
    setParentCategory(null);
    setPreviousParentCategories([]);
    setFetchedCategories({});
    setBinder(null);
  }, [workspaceId]);

  // Fetch workspaces when org is selected; auto-select if only one
  useEffect(() => {
    if (organizationId) {
      getWorkspaces({ organizationId }).then(data => {
        if (data?.length === 1) {
          replaceParamInUrl(INTERNAL_ROUTING.QUERY_PARAMS.HUB_WORKSPACE, data[0].id);
        }
      });
    }
  }, [organizationId, getWorkspaces, replaceParamInUrl]);

  // Fetch categories when workspace is selected
  useEffect(() => {
    if (organizationId && workspaceId) {
      getKnowledgeCategories({ organizationId, workspaceId, parentId: categoryId });
    }
  }, [organizationId, workspaceId, categoryId, getKnowledgeCategories]);

  // Fetch binder when selected
  useEffect(() => {
    if (binderId) {
      getBinder({ organizationId, workspaceId, binderId });
    }
  }, [binderId, organizationId, workspaceId, getBinder]);

  /**
   * event handlers
   */

  const onSelectOrganization = e => {
    const orgId = e.currentTarget.id;
    addParamToUrl(INTERNAL_ROUTING.QUERY_PARAMS.HUB_ORGANIZATION, orgId);

    const organization = organizationsList?.find(org => org.id === orgId);

    analyticsTriggers?.onSelectOrganization({
      hubOrganizationId: orgId,
      hubOrganizationName: organization?.name,
      hubOrganizationHealthSystem: organization?.healthSystem,
      hubOrganizationDepartment: organization?.department,
      isPublic: organization?.isPublicContentProvider ?? false,
    });
  };

  const onSelectWorkspace = e => {
    addParamToUrl(INTERNAL_ROUTING.QUERY_PARAMS.HUB_WORKSPACE, e.currentTarget.id);
  };

  const onSelectCategory = e => {
    setSearchParams(prev => {
      // close binder if open
      prev.delete(INTERNAL_ROUTING.QUERY_PARAMS.HUB);
      prev.set(INTERNAL_ROUTING.QUERY_PARAMS.HUB_CATEGORY, e.currentTarget.id);
      return prev;
    });
  };

  const onSelectBinder = e => {
    addParamToUrl(INTERNAL_ROUTING.QUERY_PARAMS.HUB, e.currentTarget.id);
  };

  const selectedOrganization = useMemo(
    () => organizationsList?.find(org => org.id === organizationId),
    [organizationsList, organizationId],
  );

  const value = {
    organizationsList,
    organizationId,
    selectedOrganization,
    workspacesList,
    workspaceId,
    knowledgeCategories,
    categoryId,
    sectionTitle,
    parentCategory,
    previousParentCategories,
    loading,
    loadingBinder,
    error,
    binder,
    searchParams,
    getOrganizations,
    getWorkspaces,
    getCategory,
    onSelectOrganization,
    onSelectWorkspace,
    onSelectCategory,
    onSelectBinder,
    analyticsTriggers,
  };

  return <KnowledgeHubContext.Provider value={value}>{children}</KnowledgeHubContext.Provider>;
}
